import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, LoadMore, PageHeader, SegmentTabs, Spinner, usePagedList } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, userService } from "../../services/userApi";

const money = (v) => Number((Number(v) || 0).toFixed(2)).toLocaleString();

// Lawyers whose service the client bought; each opens its service chat.
const UserMyLawyers = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("status") === "inactive" ? "inactive" : "active";
  const [opening, setOpening] = useState(null);

  const list = usePagedList((page) => userService.getMyServices(tab, page), (d) => d.items, [tab]);

  const open = async (service) => {
    if (service.chat?.id) {
      navigate(`/user/chat/${service.chat.id}`);
      return;
    }
    setOpening(service.id);
    try {
      const res = await userService.openServiceChat(service.id);
      const chatId = res?.data?.data?.chat?.id;
      if (!chatId) throw new Error(res?.data?.message);
      navigate(`/user/chat/${chatId}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Unable to open the chat."));
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="lwp-page">
      <Seo title="My Lawyers | Legal Platform" noindex />
      <PageHeader title="My Lawyers" backTo="/user/dashboard" />

      <SegmentTabs
        label="Service status"
        value={tab}
        onChange={(key) => setParams(key === "active" ? {} : { status: key }, { replace: true })}
        tabs={[
          { key: "active", label: "Active", count: Number(list.meta.active_count) || 0 },
          { key: "inactive", label: "Past", count: Number(list.meta.inactive_count) || 0 },
        ]}
      />

      {list.loading ? (
        <Spinner />
      ) : list.meta.error ? (
        <EmptyState icon="bi-exclamation-circle" text={list.meta.error} />
      ) : list.items.length === 0 ? (
        <div className="usp-posts-empty">
          <EmptyState
            icon="bi-person-badge"
            text={tab === "active" ? "You haven't hired a lawyer yet." : "No past services."}
          />
          {tab === "active" && (
            <button type="button" className="lwp-btn" onClick={() => navigate("/user/lawyers")}>
              Hire Lawyers
            </button>
          )}
        </div>
      ) : (
        <>
          <ul className="lwp-card-list">
            {list.items.map((s) => {
              const expiry = moment(s.expiry_date);
              return (
                <li key={s.id}>
                  <button type="button" className="lwp-call-row" onClick={() => open(s)} disabled={opening === s.id}>
                    <Avatar src={s.lawyer?.picture} alt={s.lawyer?.name} size={64} />
                    <span className="lwp-call-row-body">
                      <strong>{s.lawyer?.name || "Lawyer"}</strong>
                      <span>
                        {s.period === "monthly" ? "Monthly" : "One Time Service"} · $ {money(s.pay_amount)}
                      </span>
                      {expiry.isValid() && (
                        <span className="lwp-meta">
                          <i className="bi bi-calendar3" /> {s.status === "Active" ? "Renews / ends" : "Ended"}{" "}
                          {expiry.format("D MMM YYYY")}
                        </span>
                      )}
                    </span>
                    <span className="lwp-call-row-side">
                      <span className={`lwp-status lwp-status-${s.status === "Active" ? "ended" : "cancelled"}`}>{s.status}</span>
                      <span className="lwp-call-row-time">
                        {Number(s.chat_unread) > 0 && <span className="lwp-unread">{s.chat_unread}</span>}
                        {opening === s.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-chat-dots" />}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <LoadMore list={list} />
        </>
      )}
    </div>
  );
};

export default UserMyLawyers;
